import React, { useState, useEffect } from 'react';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, updateProfile, signOut } from 'firebase/auth';
import { app, db } from '../App';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { validateFirstName } from '../utils/profanityFilter';

export default function Login() {
  const navigate = useNavigate();
  const auth = getAuth(app);
  const provider = new GoogleAuthProvider();
  
  // DISABLED: Signup mode disabled for testing - only email login allowed
  const [isSignupMode, setIsSignupMode] = useState(false);
  // Force signup mode to always be false
  useEffect(() => {
    if (isSignupMode) {
      setIsSignupMode(false);
    }
  }, [isSignupMode]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  // Load saved credentials
  useEffect(() => {
    const savedEmail = localStorage.getItem('fidora_saved_email');
    const savedPassword = localStorage.getItem('fidora_saved_password');
    const savedRememberMe = localStorage.getItem('fidora_remember_me');
    
    if (savedRememberMe === 'true' && savedEmail) {
      setEmail(savedEmail);
      if (savedPassword) {
        setPassword(savedPassword);
      }
      setRememberMe(true);
    }
  }, []);

  const handleEmailSignIn = async () => {
    setAuthError('');
    setLoading(true);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      
      // Check if user is in admin collection
      const adminDocRef = doc(db, 'admins', userCredential.user.uid);
      const adminDoc = await getDoc(adminDocRef);
      
      if (!adminDoc.exists() || adminDoc.data().isAdmin !== true) {
        await signOut(auth);
        setAuthError('⛔ Access Denied. Only administrators can access this application.');
        setLoading(false);
        return;
      }
      
      if (rememberMe) {
        localStorage.setItem('fidora_saved_email', email.trim());
        localStorage.setItem('fidora_saved_password', password);
        localStorage.setItem('fidora_remember_me', 'true');
      } else {
        localStorage.removeItem('fidora_saved_email');
        localStorage.removeItem('fidora_saved_password');
        localStorage.removeItem('fidora_remember_me');
      }
      
      // Redirect to app
      navigate('/app');
    } catch (error) {
      console.error('Sign in error:', error);
      setAuthError(error.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async () => {
    setAuthError('');
    
    if (!agreeToTerms) {
      setAuthError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }
    
    // Validate first name for profanity
    if (firstName.trim()) {
      const validation = validateFirstName(firstName);
      if (!validation.valid) {
        setAuthError(validation.error);
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      
      // Update profile with first name
      if (firstName.trim()) {
        await updateProfile(userCredential.user, {
          displayName: firstName.trim()
        });
      }
      
      // Check if user is in admin collection
      const adminDocRef = doc(db, 'admins', userCredential.user.uid);
      const adminDoc = await getDoc(adminDocRef);
      
      if (!adminDoc.exists() || adminDoc.data().isAdmin !== true) {
        await signOut(auth);
        setAuthError('⛔ Access Denied. Only administrators can access this application. Please contact support to get admin access.');
        setLoading(false);
        return;
      }
      
      if (rememberMe) {
        localStorage.setItem('fidora_saved_email', email.trim());
        localStorage.setItem('fidora_saved_password', password);
        localStorage.setItem('fidora_remember_me', 'true');
      }
      
      // Redirect to app
      navigate('/app');
    } catch (error) {
      console.error('Sign up error:', error);
      setAuthError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthError('');
    setLoading(true);
    
    try {
      if (isSignupMode && !agreeToTerms) {
        setAuthError('Please agree to the Terms of Service and Privacy Policy');
        setLoading(false);
        return;
      }
      
      const result = await signInWithPopup(auth, provider);
      
      // Validate display name from Google sign-in for profanity
      if (result.user && result.user.displayName) {
        const validation = validateFirstName(result.user.displayName.split(' ')[0] || result.user.displayName);
        if (!validation.valid) {
          // If profanity detected, prompt user to change it
          const newName = prompt('Please enter a different display name (no inappropriate language):');
          if (newName) {
            const newValidation = validateFirstName(newName);
            if (newValidation.valid) {
              await updateProfile(result.user, { displayName: newName });
            } else {
              setAuthError(newValidation.error);
              await signOut(auth);
              setLoading(false);
              return;
            }
          } else {
            await signOut(auth);
            setAuthError('Please use an appropriate display name.');
            setLoading(false);
            return;
          }
        }
      }
      
      // Check if user is in admin collection
      const adminDocRef = doc(db, 'admins', result.user.uid);
      const adminDoc = await getDoc(adminDocRef);
      
      if (!adminDoc.exists() || adminDoc.data().isAdmin !== true) {
        await signOut(auth);
        setAuthError('⛔ Access Denied. Only administrators can access this application.');
        setLoading(false);
        return;
      }
      
      // Redirect to app
      navigate('/app');
    } catch (error) {
      console.error('Google sign in error:', error);
      setAuthError(error.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSignupMode) {
      handleEmailSignUp();
    } else {
      handleEmailSignIn();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  // Validation states
  const isFormValid = () => {
    // Email and password are required
    if (!email.trim() || !password.trim()) {
      return false;
    }
    
    // For signup mode, additional checks
    if (isSignupMode) {
      // First name validation (if provided)
      if (firstName.trim()) {
        const validation = validateFirstName(firstName);
        if (!validation.valid) {
          return false;
        }
      }
      // Terms must be agreed to
      if (!agreeToTerms) {
        return false;
      }
    }
    
    return true;
  };

  const isButtonDisabled = loading || !isFormValid();

  return (
    <div className="login-container" style={{ position: 'relative' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        style={{
          position: 'absolute',
          top: '24px',
          left: '24px',
          background: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '12px',
          padding: '10px 20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.95rem',
          fontWeight: 500,
          color: '#374151',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(10px)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#ffffff';
          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.15)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
          e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back
      </button>
      
      <div className="login-card">
        <img src="/fidora_fav1.png" alt="Fidora" style={{ width: 200, height: 200, marginBottom: 24 }} />
        
        {authError && (
          <div style={{
            background: '#fee',
            color: '#c33',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '0.9rem'
          }}>
            {authError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {isSignupMode && (
            <>
              <input
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  // Clear error when user starts typing
                  if (authError && authError.includes('name')) {
                    setAuthError('');
                  }
                }}
                onKeyPress={handleKeyPress}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '12px',
                  borderRadius: '8px',
                  border: firstName.trim() && validateFirstName(firstName).valid === false 
                    ? '1px solid #ef4444' 
                    : '1px solid #ddd',
                  fontSize: '1rem'
                }}
                autoComplete="given-name"
              />
              {firstName.trim() && !validateFirstName(firstName).valid && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '0.85rem',
                  marginTop: '-8px',
                  marginBottom: '12px'
                }}>
                  {validateFirstName(firstName).error}
                </div>
              )}
            </>
          )}
          
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              // Clear error when user starts typing
              if (authError) {
                setAuthError('');
              }
            }}
            onKeyPress={handleKeyPress}
            style={{
              width: '100%',
              padding: '12px',
              marginBottom: '12px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
            autoComplete="email"
            required
          />
          
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                // Clear error when user starts typing
                if (authError) {
                  setAuthError('');
                }
              }}
              onKeyPress={handleKeyPress}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '40px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '1rem'
              }}
              autoComplete={isSignupMode ? "new-password" : "current-password"}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#666'
              }}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              )}
            </button>
          </div>

          {!isSignupMode && (
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              <label htmlFor="rememberMe" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                Remember me
              </label>
            </div>
          )}

          {isSignupMode && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={agreeToTerms}
                  onChange={(e) => {
                    setAgreeToTerms(e.target.checked);
                    // Clear error when user checks the box
                    if (authError && authError.includes('Terms')) {
                      setAuthError('');
                    }
                  }}
                  style={{ marginRight: '8px', marginTop: '4px' }}
                  required
                />
                <label htmlFor="agreeToTerms" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" style={{ color: '#6366f1' }}>Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" style={{ color: '#6366f1' }}>Privacy Policy</a>
                </label>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isButtonDisabled}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '8px',
              border: 'none',
              background: isButtonDisabled ? '#9ca3af' : '#6366f1',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
              opacity: isButtonDisabled ? 0.6 : 1,
              marginBottom: '12px',
              transition: 'all 0.2s ease'
            }}
          >
            {loading ? 'Please wait...' : (isSignupMode ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        {/* DISABLED: Google login and account creation for testing */}
        {/* Google Sign-in button removed */}
        {/* Account creation toggle removed */}

        {!isSignupMode && (
          <div style={{ marginTop: '12px', textAlign: 'center' }}>
            <button
              onClick={() => navigate('/reset-password')}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '0.85rem',
                textDecoration: 'underline'
              }}
            >
              Forgot password?
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
